/* SeguroManualPanel.jsx — seguro capturado a mano por un admin cuando RODAC no
 * cotiza el auto ("Este auto requiere cotización con asesor").
 * P4 de backend/docs/plans/admin-recotizar-placa-seguro-manual.md (D-ADM-8/9).
 *
 * Lo pinta CalcStack debajo del ResultStrip, solo para admin Pei (el backend
 * responde 403 a los demás). Lo capturado vive en el hook (h.seguroManual) para
 * no perderse si el panel se desmonta mientras recotiza:
 *   h.editarSeguroManual(patch) · h.aplicarSeguroManual() · h.quitarSeguroManual()
 */
import { useEffect, useState } from 'react';
import { Dropdown, PriceField } from './HeroShared';
import { fmtMXN } from '../../core/heroCore';
import { fetchAseguradoras } from '../../../../services/adminRecotizacionApi';
import {
  generarFolio, plazosDelSeguro, primaMensual, validarSeguroManual, MAX_ASEGURADORA, MAX_FOLIO,
} from '../../../../Utils/seguroManual';

const OTRA = '__otra__'; // "Otra…": no choca con ningún value de la API

export default function SeguroManualPanel({ h }) {
  const sm = h.seguroManual;
  const plazo = h.state.plazo;
  const busy = !!h.quote.loading;
  const [lista, setLista] = useState(null);      // [{value, label}] · null = cargando
  const [otra, setOtra] = useState(false);       // eligió "Otra…" en la lista
  const [intento, setIntento] = useState(false); // ya pulsó "Calcular" → errores en vivo

  /* GET admin/cotizaciones/aseguradoras al abrir el panel (solo sale para admin).
   * La API la pide una vez por página y la comparte entre montajes. */
  useEffect(() => {
    let vivo = true;
    fetchAseguradoras()
      .then((l) => { if (vivo) setLista(l); })
      // sin lista (falló o vino vacía) → solo el campo de texto
      .catch(() => { if (vivo) setLista([]); });
    return () => { vivo = false; };
  }, []);

  const conLista = Array.isArray(lista) && lista.length > 0;
  const enLista = conLista && lista.some(o => o.value === sm.company);
  // texto libre: sin lista, con "Otra…" elegida o si lo capturado no viene en la lista
  const texto = lista !== null && (!conLista || otra || (!!sm.company && !enLista));

  const v = validarSeguroManual({ plazo, company: sm.company, montos: sm.montos, folios: sm.folios, foliosAuto: sm.foliosAuto });
  // errores por campo: tras pulsar "Calcular", o si el manual aplicado no cubre el
  // plazo nuevo (la renta volvió a "requiere cotización con asesor")
  const verErrores = intento || (sm.aplicado && h.quote.noInsurance === 'auto');
  const err = verErrores ? v.errores : { montos: {}, folios: {} };

  const onAseguradora = (val) => {
    if (val === OTRA) {
      setOtra(true);
      if (enLista) h.editarSeguroManual({ company: '' });
      return;
    }
    setOtra(false);
    h.editarSeguroManual({ company: val });
  };
  // PriceField da 0 con el campo vacío: se guarda '' para pedir "Captura la prima…"
  const setPrima = (p, n) => h.editarSeguroManual(s => ({ montos: { ...s.montos, [p]: n || '' } }));
  // cada plazo es su propia fila de seguro: la aseguradora da un folio por plazo.
  // Escribirlo a mano lo vuelve de la aseguradora; "No lo tengo" genera uno.
  const setFolio = (p, valor) => h.editarSeguroManual(s => ({
    folios: { ...s.folios, [p]: valor }, foliosAuto: { ...s.foliosAuto, [p]: false },
  }));
  const sinFolio = (p) => h.editarSeguroManual(s => ({
    folios: { ...s.folios, [p]: generarFolio() }, foliosAuto: { ...s.foliosAuto, [p]: true },
  }));
  const calcular = () => {
    setIntento(true);
    if (v.ok) h.aplicarSeguroManual();
  };

  return (
    <div className="smp">
      <div className="smp-head">
        <span className="smp-t">Seguro manual</span>
        <span className="smp-badge">solo admin</span>
      </div>
      <p className="smp-help">RODAC no cotizó este auto. Si ya lo cotizaste con la aseguradora, captura la prima total con IVA de cada plazo.</p>

      <div className="smp-row">
        <div className="fld">
          <div className="lab">Aseguradora</div>
          {(lista === null || conLista) && (
            <div className={`smp-dd${err.company && !texto ? ' invalid' : ''}`}>
              <Dropdown
                value={texto ? OTRA : sm.company}
                options={[...(lista || []), { value: OTRA, label: 'Otra…' }]}
                placeholder={lista === null ? 'Cargando…' : 'Elige…'}
                disabled={lista === null}
                onChange={onAseguradora}
                compact
                up
              />
            </div>
          )}
          {texto && (
            <input
              className={`smp-in${err.company ? ' invalid' : ''}`}
              value={sm.company}
              maxLength={MAX_ASEGURADORA}
              placeholder="Nombre de la aseguradora"
              aria-label="Nombre de la aseguradora"
              onChange={(e) => h.editarSeguroManual({ company: e.target.value })}
            />
          )}
          {err.company && <div className="smp-err">{err.company}</div>}
        </div>
      </div>

      <div className="smp-primas">
        {plazosDelSeguro(plazo).map((p) => {
          const monto = sm.montos?.[p];
          const e = err.montos?.[p];
          const eFolio = err.folios?.[p];
          const auto = !!sm.foliosAuto?.[p];
          return (
            <div className="fld" key={p}>
              <div className="lab">Prima {p} meses</div>
              <PriceField value={+monto || 0} placeholder="$0" invalid={!!e} ariaLabel={`Prima ${p} meses`} onChange={(n) => setPrima(p, n)} />
              {e
                ? <div className="smp-err">{e}</div>
                : <div className="smp-hint">{+monto > 0 ? `≈ ${fmtMXN(primaMensual(monto, p))}/mes` : ''}</div>}
              <input
                className={`smp-in${eFolio ? ' invalid' : ''}`}
                value={sm.folios?.[p] || ''}
                maxLength={MAX_FOLIO}
                placeholder="Folio de la aseguradora"
                aria-label={`Folio ${p} meses`}
                onChange={(ev) => setFolio(p, ev.target.value)}
              />
              {eFolio
                ? <div className="smp-err">{eFolio}</div>
                : auto && <div className="smp-hint">Generado: no es de la aseguradora</div>}
              <button
                type="button"
                className="smp-sin-folio"
                aria-label={auto ? `Generar otro folio de ${p} meses` : `No tengo el folio de ${p} meses`}
                onClick={() => sinFolio(p)}
              >
                {auto ? 'Generar otro' : 'No lo tengo'}
              </button>
            </div>
          );
        })}
      </div>

      <div className="smp-acts">
        {sm.aplicado && (
          <button type="button" className="smp-quitar" disabled={busy} onClick={h.quitarSeguroManual}>Quitar seguro manual</button>
        )}
        <button type="button" className="smp-go" disabled={busy} onClick={calcular}>Calcular renta con este seguro</button>
      </div>
    </div>
  );
}
